import React, { useState } from "react";
import { Divider, MenuItem, Select, Stack } from "@mui/material";
import { QueryFieldFilterTypes, QueryInfo, QueryField, socialUniverseField } from "system/query/QueryInfo";
import MentionsOverTimeDisplay from "./MentionsOverTimeDisplay";
import LeafAggSelector from "components/widgetParts/LeafAggSelector";
import { Agg } from "system/query/Aggs";
import TabbedDialog from "components/dialogs/TabbedDialog";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SettingsColorPicker from "components/settingsConfig/uiComponents/SettingsColorPicker";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";

const defaultWidgetRequest = {
  name: 'mentions-over-time',
  endpoint: 'query/agg',
  queryParams: {
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

const defaultRangesRequest = {
  name: 'mentions-over-time-ranges',
  endpoint: 'query/ranges',
  queryParams: {
    field: 'published'
  },
  requestInit: {},
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultMentionsOverTimeRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;
  const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };

  const requestInitBody = { aggs: {} };
  if (foundSocialUniverseField) {
    requestInitBody.aggs = {
      [socialUniverseField.label]: {
        op: socialUniverseField.id
      }
    };
  }
  widgetRequest.requestInit = { body: requestInitBody };

  const rangesRequest: WidgetRequest = { ...defaultRangesRequest };
  return [widgetRequest, rangesRequest];
}

export type MentionsOverTimeSettingsStateType = {
  customWidgetTitle: string;
  widgetTitle: string;
  widgetTitleAlignment: string;
  color?: string;
  alignment: string;
  showDecimals: boolean;
  frequencyUnit: string;
};

export const defaultSettings: MentionsOverTimeSettingsStateType = {
  customWidgetTitle: '',
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
  alignment: 'left',
  showDecimals: true,
  frequencyUnit: 'auto'
};

const frequencyUnits = ['second', 'minute', 'hour', 'day', 'week', 'month', 'year'];

export default function MentionsOverTimeSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'mentions-over-time');
  const requestBody = originalRequest?.requestInit?.body || {};

  const keys = Object.keys(requestBody?.aggs || {});
  let firstAggKey = "";
  let aggDefinition = null;
  if (keys.length) {
    firstAggKey = keys[0];
    aggDefinition = requestBody.aggs[firstAggKey];
  }

  const [aggsLabel, setAggsLabel] = useState<string>(firstAggKey);
  const [localAggs, setLocalAggs] = useState<Agg | undefined>(aggDefinition || undefined);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

  const [settingsState, setSettingsState] = useState<MentionsOverTimeSettingsStateType>({...defaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: MentionsOverTimeSettingsStateType) => setSettingsState(updatedState);

  const updateWidget = () => {
    const label:string = aggsLabel || localAggs?.field || "total";
    const newRequestInfo = localAggs ? {body: {aggs: { [label]: localAggs }}} : {body: {aggs: {}}};


    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = { aggs: {} };

    if (localAggs) {
      requestInitBody.aggs = { [label]: localAggs };
    }

    widgetRequest.requestInit = { body: requestInitBody };

    const rangesRequest : WidgetRequest = { ...defaultRangesRequest };
    props.saveWidgetDataAndRequests({
      request_info: newRequestInfo,
      settings: settingsState
    }, [widgetRequest, rangesRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }

  return (
    <TabbedDialog
      title={i18n.t("widget-types.mentions-over-time.settings.title")}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      open={true}
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
    }}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            <SimpleFormLabel>{i18n.t("category")}</SimpleFormLabel>
            <LeafAggSelector
              allowedFieldTypes={[QueryFieldFilterTypes.record]}
              queryInfo={props.queryInfo}
              aggs={localAggs}
              setAggs={(newAggs: Agg | null) => setLocalAggs(newAggs || undefined)}
              aggsLabel={aggsLabel}
              setAggsLabel={setAggsLabel}
              fixedOperator="sum"
              ></LeafAggSelector>
            <SimpleFormLabel>{i18n.t("widget-types.mentions-over-time.settings.frequency-unit")}</SimpleFormLabel>
            <Select
              fullWidth
              value={settingsState.frequencyUnit}
              onChange={(e) => setSettingsState({ ...settingsState, frequencyUnit: e.target.value })}>
              <MenuItem value="auto">{i18n.t("auto")}</MenuItem>
              {frequencyUnits.map(unit => (
                <MenuItem key={unit} value={unit}>{i18n.t(unit)}</MenuItem>
              ))}
            </Select>
          </>)}
        </>)}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Preview */}
            <MentionsOverTimeDisplay settingsState={settingsState} isPreview={true} />
            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle}
                />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={i18n.t("widget-settings.custom-text")}
                      value={settingsState.customWidgetTitle} />
                  </Stack>
                }
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
              <SettingsColorPicker
                color={settingsState.color ?? ''}
                defaultColor={theme.colors.primary.dark}
                keyid="color"
                label={i18n.t("color")} />
              <SettingsToggleButton
                  keyid="alignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.alignment} />
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
      </>
    </TabbedDialog>
  );
}
